/**
 * Enhanced Input Validation for Security
 * Comprehensive validation to prevent injection attacks and ensure data integrity
 */

import { SECURITY_CONFIG } from './securityConfig';

export interface ValidationResult {
  isValid: boolean;
  sanitized: string;
  errors: string[];
}

/**
 * Enhanced input validation with comprehensive security checks
 */
export function validateAndSanitizeInput(
  input: string,
  fieldName: string,
  maxLength?: number
): ValidationResult {
  const errors: string[] = [];
  let sanitized = input.trim();
  
  // Check for null/undefined
  if (!input || input.trim().length === 0) {
    return {
      isValid: false,
      sanitized: '',
      errors: [`${fieldName} est requis`]
    };
  }

  // Length validation
  const limit = maxLength || SECURITY_CONFIG.CONTENT_SECURITY.MAX_STRING_LENGTH;
  if (sanitized.length > limit) {
    errors.push(`${fieldName} ne peut pas dépasser ${limit} caractères`);
    sanitized = sanitized.substring(0, limit);
  }

  // Check for dangerous patterns
  const hasDangerousContent = SECURITY_CONFIG.CONTENT_SECURITY.DANGEROUS_PATTERNS.some(
    pattern => pattern.test(sanitized)
  );
  
  if (hasDangerousContent) {
    errors.push(`${fieldName} contient du contenu non autorisé`);
    // Remove potentially dangerous content
    sanitized = sanitized
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<[^>]*>/g, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+=/gi, '');
  }

  // Additional sanitization for special characters
  sanitized = sanitized
    .replace(/[<>'"&]/g, (char) => {
      switch (char) {
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '"': return '&quot;';
        case "'": return '&#x27;';
        case '&': return '&amp;';
        default: return char;
      }
    });

  return {
    isValid: errors.length === 0,
    sanitized,
    errors
  };
}

/**
 * Enhanced email validation
 */
export function validateEmail(email: string): ValidationResult {
  const baseValidation = validateAndSanitizeInput(
    email, 
    'Email', 
    SECURITY_CONFIG.FORM_LIMITS.EMAIL_MAX
  );
  
  if (!baseValidation.isValid) {
    return baseValidation;
  }

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(baseValidation.sanitized)) {
    return {
      isValid: false,
      sanitized: baseValidation.sanitized,
      errors: ['Format d\'email invalide']
    };
  }

  return baseValidation;
}

/**
 * Enhanced phone validation
 */
export function validatePhone(phone: string): ValidationResult {
  const baseValidation = validateAndSanitizeInput(
    phone, 
    'Téléphone', 
    SECURITY_CONFIG.FORM_LIMITS.PHONE_MAX
  );
  
  if (!baseValidation.isValid) {
    return baseValidation;
  }

  // Allow French and international phone formats
  const phoneRegex = /^(?:\+33|0)[1-9](?:[0-9]{8})$|^(?:\+\d{1,3})?[-.\s]?\(?\d{1,4}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/;
  if (!phoneRegex.test(baseValidation.sanitized.replace(/[\s.-]/g, ''))) {
    return {
      isValid: false,
      sanitized: baseValidation.sanitized,
      errors: ['Format de téléphone invalide']
    };
  }

  return baseValidation;
}

/**
 * Project type validation
 */
export function validateProjectType(projectType: string): ValidationResult {
  const allowedProjectTypes = [
    'construction-neuve',
    'renovation-lourde',
    'extension',
    'surelevation',
    'maison-individuelle',
    'copropriete',
    'autre'
  ];

  const baseValidation = validateAndSanitizeInput(
    projectType, 
    'Type de projet', 
    SECURITY_CONFIG.FORM_LIMITS.PROJECT_TYPE_MAX
  );
  
  if (!baseValidation.isValid) {
    return baseValidation;
  }

  if (!allowedProjectTypes.includes(baseValidation.sanitized)) {
    return {
      isValid: false,
      sanitized: baseValidation.sanitized,
      errors: ['Type de projet non autorisé']
    };
  }

  return baseValidation;
}

/**
 * Comprehensive form validation
 */
export function validateContactForm(formData: {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  project_type: string;
  location?: string;
  work_amount?: string;
  description?: string;
}): { isValid: boolean; errors: Record<string, string[]>; sanitizedData: any } {
  const errors: Record<string, string[]> = {};
  const sanitizedData: any = {};

  // Validate required fields
  const firstNameValidation = validateAndSanitizeInput(
    formData.first_name, 
    'Prénom', 
    SECURITY_CONFIG.FORM_LIMITS.FIRST_NAME_MAX
  );
  if (!firstNameValidation.isValid) {
    errors.first_name = firstNameValidation.errors;
  }
  sanitizedData.first_name = firstNameValidation.sanitized;

  const lastNameValidation = validateAndSanitizeInput(
    formData.last_name, 
    'Nom', 
    SECURITY_CONFIG.FORM_LIMITS.LAST_NAME_MAX
  );
  if (!lastNameValidation.isValid) {
    errors.last_name = lastNameValidation.errors;
  }
  sanitizedData.last_name = lastNameValidation.sanitized;

  const emailValidation = validateEmail(formData.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.errors;
  }
  sanitizedData.email = emailValidation.sanitized;

  const phoneValidation = validatePhone(formData.phone);
  if (!phoneValidation.isValid) {
    errors.phone = phoneValidation.errors;
  }
  sanitizedData.phone = phoneValidation.sanitized;

  const projectTypeValidation = validateProjectType(formData.project_type);
  if (!projectTypeValidation.isValid) {
    errors.project_type = projectTypeValidation.errors;
  }
  sanitizedData.project_type = projectTypeValidation.sanitized;

  // Validate optional fields
  if (formData.location) {
    const locationValidation = validateAndSanitizeInput(
      formData.location, 
      'Localisation', 
      SECURITY_CONFIG.FORM_LIMITS.LOCATION_MAX
    );
    if (!locationValidation.isValid) {
      errors.location = locationValidation.errors;
    }
    sanitizedData.location = locationValidation.sanitized;
  }

  if (formData.work_amount) {
    const workAmountValidation = validateAndSanitizeInput(
      formData.work_amount, 
      'Montant des travaux', 
      SECURITY_CONFIG.FORM_LIMITS.WORK_AMOUNT_MAX
    );
    if (!workAmountValidation.isValid) {
      errors.work_amount = workAmountValidation.errors;
    }
    sanitizedData.work_amount = workAmountValidation.sanitized;
  }

  if (formData.description) {
    const descriptionValidation = validateAndSanitizeInput(
      formData.description, 
      'Description', 
      SECURITY_CONFIG.FORM_LIMITS.DESCRIPTION_MAX
    );
    if (!descriptionValidation.isValid) {
      errors.description = descriptionValidation.errors;
    }
    sanitizedData.description = descriptionValidation.sanitized;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    sanitizedData
  };
}